"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

import { richTextComponent } from "@/sanity/sanityPortableText";
import { cn } from "@/lib/utils";

import { PipeChip } from "@/components/global/sections/home/_shared";
import { FadeIn } from "@/components/global/utils/FadeIn";

import { FitHeadline } from "./FitHeadline";

import type { IRewardTier } from "@/sanity/pages/PageLeaderboardTemplate";

const RING_R = 48.53;
const RING_W = 2.934;
const RING_C = 2 * Math.PI * RING_R;
const ENTER_SPAN = 0.3;
const BEFORE_CENTRE = 0.12;
const SEG_GAP = 2.2;
const REST_GAP = 5.625;
const REST_SHRINK = 0.1;

type Segment = { start: number; full: number; from: number; to: number };

const TAB_FILLS = [
  "linear-gradient(185.93deg, #c49337 13.684%, #fcf7cd 45.085%, #edcd78 92.099%, #a7602b 120.95%, #fdf8d0 162.62%, #bb9138 200.02%)",
  "linear-gradient(185.93deg, #8f8f8f 13.684%, #fafafa 45.085%, #d6d6d6 92.099%, #6f6f6f 120.95%, #fcfcfc 162.62%, #9a9a9a 200.02%)",
  "linear-gradient(185.93deg, #96613c 13.684%, #f5d7be 45.085%, #dcaa82 92.099%, #82502d 120.95%, #f8dcc3 162.62%, #915f37 200.02%)",
  "#98d8ff",
  "#ffda2c",
];
const tabFill = (i: number) => TAB_FILLS[i] ?? "#98d8ff";

const ARC_STOPS: [number, string][][] = [
  [[0, "#c49337"], [0.45, "#fcf7cd"], [0.92, "#edcd78"], [1, "#a7602b"]],
  [[0, "#8f8f8f"], [0.45, "#fafafa"], [0.92, "#d6d6d6"], [1, "#6f6f6f"]],
  [[0, "#96613c"], [0.45, "#f5d7be"], [0.92, "#dcaa82"], [1, "#82502d"]],
  [[0, "#98d8ff"], [1, "#98d8ff"]],
  [[0, "#ffda2c"], [1, "#ffda2c"]],
];
const arcStroke = (i: number) => `url(#pool-arc-${Math.min(Math.max(i, 0), ARC_STOPS.length - 1)})`;

const usd = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;
const digits = (n: number) => Math.round(n).toLocaleString("en-US");

const REEL = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
const REEL_STEP = 1.12;

function DigitReel({ digit, delay, still }: { digit: number; delay: number; still: boolean }) {
  return (
    <span className="relative -mb-[0.12em] inline-block h-[1.12em] overflow-hidden align-top">
      <span className="invisible block">0</span>
      <span
        className="absolute inset-x-0 top-0"
        style={{
          transform: `translateY(-${digit * REEL_STEP}em)`,
          transition: still ? undefined : `transform 700ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
        }}
      >
        {REEL.map((d) => (
          <span key={d} className="block h-[1.12em]">
            {d}
          </span>
        ))}
      </span>
    </span>
  );
}

function Odometer({ value, still }: { value: number; still: boolean }) {
  const chars = digits(value).split("");
  return (
    <span className="inline-flex text-white">
      {chars.map((c, i) => {
        const fromRight = chars.length - 1 - i;
        return /\d/.test(c) ? (
          <DigitReel key={fromRight} digit={Number(c)} delay={fromRight * 40} still={still} />
        ) : (
          <span key={fromRight}>{c}</span>
        );
      })}
    </span>
  );
}

function PoolRing({
  ringRef,
  fillEls,
  segments,
  amount,
  still,
  poolLabel,
  poolTotal,
  poolPlaces,
}: {
  ringRef?: React.RefObject<HTMLDivElement>;
  fillEls: React.MutableRefObject<(SVGCircleElement | null)[]>;
  segments: Segment[];
  amount: number;
  still: boolean;
  poolLabel?: string;
  poolTotal: number;
  poolPlaces: number;
}) {
  return (
    <div ref={ringRef} className="relative h-full w-full">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90 transform-gpu" aria-hidden>
        <defs>
          {ARC_STOPS.map((stops, i) => (
            <linearGradient key={i} id={`pool-arc-${i}`} x1="0" y1="0" x2="0" y2="1">
              {stops.map(([offset, color], j) => (
                <stop key={j} offset={offset} stopColor={color} />
              ))}
            </linearGradient>
          ))}

          <filter id="pool-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.6" edgeMode="duplicate" />
          </filter>
        </defs>

        {segments.map((seg, i) => (
          <circle
            key={`ghost-${i}`}
            cx="50"
            cy="50"
            r={RING_R}
            fill="none"
            stroke={arcStroke(i)}
            strokeOpacity={0.15}
            strokeWidth={RING_W}
            strokeDasharray={`${seg.full} ${RING_C}`}
            strokeDashoffset={-seg.start}
          />
        ))}

        <defs>
          <g id="pool-fills">
            {segments.map((seg, i) => (
              <circle
                key={`fill-${i}`}
                ref={(el) => {
                  fillEls.current[i] = el;
                }}
                cx="50"
                cy="50"
                r={RING_R}
                fill="none"
                stroke={arcStroke(i)}
                strokeWidth={RING_W}
              />
            ))}
          </g>
        </defs>

        <use href="#pool-fills" filter="url(#pool-glow)" opacity={0.5} />
        <use href="#pool-fills" />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="font-geist text-[1.75rem] uppercase leading-[2.125rem] tracking-[0.275rem] text-white/[0.46]">
          {poolLabel}
        </span>
        <p className="m-0 flex items-start pt-[1.775rem] font-geist text-[7rem] font-semibold leading-none tracking-[-0.3086rem] tabular-nums lg:text-[10.28rem]">
          <span className="font-jetbrainsmono font-medium text-white/[0.46]">$</span>
          <Odometer value={amount} still={still} />
        </p>
        <span className="pt-[1.3rem] font-geist text-[1.75rem] leading-[2.325rem] text-white/[0.62]">
          {usd(poolTotal)} across {poolPlaces} places
        </span>
      </div>
    </div>
  );
}

function TierCard({ tier, index }: { tier: IRewardTier; index: number }) {
  return (
    <div className="relative w-full">
      <div className="flex items-end">
        <div
          className="flex items-center justify-center rounded-t-[0.5rem] px-[2.5rem] py-[0.875rem]"
          style={{ background: tabFill(index) }}
        >
          <span className="whitespace-nowrap font-geist text-[1.375rem] font-medium uppercase leading-none tracking-[0.1925rem] text-black">
            {tier.label}
          </span>
        </div>
        {index < 3 && (
          <span className="h-px min-w-0 flex-1" style={{ background: tabFill(index) }} />
        )}
      </div>

      <div className="flex min-w-0 flex-col items-start gap-[1.9375rem] rounded-b-[1rem] rounded-tr-[1rem] border border-white/[0.11] bg-[#0d0d0d] px-[2.75rem] pb-[2.75rem] pt-[3.75rem]">
        {tier.percentOfPool != null && (
          <p className="m-0 font-geist text-[1.5rem] uppercase leading-[2.325rem] tracking-[0.18rem] text-white/[0.62]">
            {tier.percentOfPool}% of the pool
          </p>
        )}

        <div className="flex w-full flex-wrap items-center gap-x-[1.5rem]">
          {tier.amount != null && (
            <span className="font-geist text-[4rem] font-medium leading-[6.2rem] tracking-[-0.08rem] tabular-nums text-white">
              {usd(tier.amount)}
            </span>
          )}
          {tier.winners != null && (
            <span className="flex shrink-0 items-center rounded-[0.625rem] bg-white/[0.15] px-[1.375rem] py-[1rem] font-geist text-[1.5rem] leading-none text-white/[0.72]">
              {tier.winners} winner{tier.winners === 1 ? "" : "s"}
            </span>
          )}
        </div>

        {tier.lines && tier.lines.length > 0 && (
          <div className="flex w-full min-w-0 flex-col gap-[1rem] border-t border-white/[0.11] pt-[2.3125rem]">
            {tier.lines.map((line, i) => (
              <div key={i} className="flex w-full min-w-0 items-center justify-between gap-[1.5rem]">
                <div className="flex min-w-0 items-center gap-[1.25rem]">
                  <span className="flex h-[3.25rem] w-[3.25rem] shrink-0 items-center justify-center rounded-[0.5rem] bg-white/[0.15]">
                    {line.icon?.src && (
                      <Image src={line.icon.src} alt="" width={line.icon.width} height={line.icon.height} className="h-[1.75rem] w-auto" />
                    )}
                  </span>
                  <span className="min-w-0 truncate font-geist text-[1.5rem] leading-[2.325rem] text-white/[0.46]">
                    {line.label}
                  </span>
                </div>
                <span className="shrink-0 whitespace-nowrap font-geist text-[1.5rem] leading-[2.325rem] text-white">
                  {line.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function MonthlyRewards({
  chip,
  title,
  subtitle,
  poolLabel,
  poolTotal = 0,
  poolPlaces = 0,
  tiers,
}: {
  chip?: string;
  title?: PortableTextBlock[];
  subtitle?: string;
  poolLabel?: string;
  poolTotal?: number;
  poolPlaces?: number;
  tiers?: IRewardTier[];
}) {
  const items = (tiers ?? []).filter(Boolean);
  const runwayRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const fillEls = useRef<(SVGCircleElement | null)[]>([]);
  const cardEls = useRef<(HTMLDivElement | null)[]>([]);

  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const cumulative = useMemo(() => {
    let sum = 0;
    return items.map((t) => (sum += t.amount ?? 0));
  }, [tiers]);

  const segments = useMemo<Segment[]>(() => {
    const total = poolTotal > 0 ? poolTotal : (cumulative[cumulative.length - 1] ?? 0);
    if (!total) return [];
    let acc = 0;
    return items.map((t) => {
      const from = acc;
      acc += t.amount ?? 0;
      return {
        from,
        to: acc,
        start: (RING_C * from) / total,
        full: Math.max((RING_C * ((acc - from) / total)) - SEG_GAP, 0),
      };
    });
  }, [tiers, poolTotal, cumulative]);

  const [amount, setAmount] = useState(0);

  useEffect(() => {
    if (!items.length) return;

    const paint = (ts: number[]) => {
      segments.forEach((seg, i) => {
        const el = fillEls.current[i];
        if (!el) return;
        const t = Math.min(Math.max(ts[i] ?? 0, 0), 1);
        const dash = `${t * seg.full} ${RING_C}`;
        const offset = `${-seg.start}`;
        if (el.getAttribute("stroke-dasharray") !== dash) el.setAttribute("stroke-dasharray", dash);
        if (el.getAttribute("stroke-dashoffset") !== offset) el.setAttribute("stroke-dashoffset", offset);
      });
    };

    const HOLD = 0.96;
    let counted = 0;

    const narrow = window.matchMedia("not all and (min-width: 993px)");

    const ringBox = () => {
      const el = ringRef.current;
      if (!el) return null;
      const { height } = el.getBoundingClientRect();
      const parent = el.parentElement;
      const top = parent ? parseFloat(getComputedStyle(parent).top) || 0 : 0;
      return { top, bottom: top + height, height };
    };

    const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

    let rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 8;
    // The rise each card got last frame, so mobile can read its sticky top without it.
    const lift: number[] = [];

    // Whole pixels: a fractional sticky offset shimmers while scrolling.
    const measureStop = () => {
      const runway = runwayRef.current;
      if (!runway) return;
      runway.style.removeProperty("--card-stop");
      rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 8;
      const ring = ringBox();
      if (!narrow.matches || !ring) return;
      runway.style.setProperty("--card-stop", `${Math.round(ring.bottom + REST_GAP * rem)}px`);
    };

    const stopLine = () => {
      const c = cardEls.current.find(Boolean);
      return c ? parseFloat(getComputedStyle(c).top) || 0 : 0;
    };

    const layoutTop = (c: HTMLDivElement, i: number) => c.getBoundingClientRect().top - (lift[i] ?? 0);

    const creditLine = (ring: { top: number; bottom: number; height: number }) =>
      ring.top + ring.height * (0.5 - BEFORE_CENTRE);
    const cardAnchor = (r: DOMRect) => r.top + r.height / 2;

    const progress = () => {
      const ring = ringBox();
      if (!ring) return null;
      const vh = window.innerHeight;

      if (narrow.matches) {
        const stop = stopLine();
        const travel = Math.max(vh - stop, 1);
        return cardEls.current.map((el, i) => {
          if (!el) return 0;
          const top = layoutTop(el, i);
          return top <= stop + 0.5 ? 1 : clamp01((vh - top) / travel);
        });
      }

      const travel = Math.max(vh - creditLine(ring), 1);
      return cardEls.current.map((el) =>
        el
          ? Math.min(1, Math.max(0, (vh - cardAnchor(el.getBoundingClientRect())) / travel))
          : 0,
      );
    };

    const creditedFrom = (ts: number[]) => {
      let n = 0;
      ts.forEach((t, i) => {
        if (i < counted ? t >= HOLD : t >= 1) n += 1;
      });
      counted = Math.min(Math.max(n, 0), items.length);
      return counted;
    };

    const enter = (top: number, vh: number, span: number) => {
      const p = clamp01((vh - top) / span);
      return p * p * (3 - 2 * p);
    };

    // The last card fades while it rests and is gone by the time it would reach the
    // ring, so the ring still releases 8rem above the next section.
    const restFade = (c: HTMLDivElement, stop: number) => {
      const ring = ringBox();
      const runway = runwayRef.current;
      const spacer = c.parentElement?.nextElementSibling;
      if (!ring || !runway || !(spacer instanceof HTMLElement)) return 0;
      const rest = spacer.offsetHeight;
      return (stop + c.offsetHeight + rest - runway.getBoundingClientRect().bottom) / (rest + stop - ring.bottom);
    };

    const setLook = (c: HTMLDivElement, opacity: number, transform: string) => {
      const o = String(Math.round(opacity * 1000) / 1000);
      if (c.style.opacity !== o) c.style.opacity = o;
      if (c.style.transform !== transform) c.style.transform = transform;
    };

    const fadeCards = () => {
      const vh = window.innerHeight;
      const cards = cardEls.current;

      if (!narrow.matches) {
        cards.forEach((c, i) => {
          if (!c) return;
          const t = enter(c.getBoundingClientRect().top, vh, vh * ENTER_SPAN);
          lift[i] = (1 - t) * 2 * rem;
          setLook(c, t, `translateY(${((1 - t) * 2).toFixed(3)}rem)`);
        });
        return;
      }

      const stop = stopLine();
      const span = Math.max(Math.min(vh * ENTER_SPAN, vh - stop), 1);
      const tops = cards.map((c, i) => (c ? layoutTop(c, i) : 0));
      cards.forEach((c, i) => {
        if (!c) return;
        const t = enter(tops[i], vh, span);
        const next = cards[i + 1];
        // Starts where the card leaves the screen or ends, and finishes before a shorter
        // next card could uncover its bottom.
        const shown = Math.min(c.offsetHeight, vh - stop);
        const gone = clamp01(
          next
            ? (stop + shown - tops[i + 1]) / Math.max(Math.min(shown, next.offsetHeight), 1)
            : restFade(c, stop),
        );
        lift[i] = (1 - t) * 2 * rem;
        setLook(c, t * (1 - gone), `translateY(${lift[i].toFixed(2)}px) scale(${(1 - REST_SHRINK * gone).toFixed(4)})`);
      });
    };

    let tier = -1;
    paint([]);

    gsap.registerPlugin(ScrollTrigger);

    const apply = (ts: number[]) => {
      const next = creditedFrom(ts) - 1;
      if (next === tier) return;
      tier = next;
      setAmount(cumulative[next] ?? 0);
    };

    const frame = () => {
      const ts = progress();
      if (!ts) return;
      apply(ts);
      paint(ts);
      fadeCards();
    };

    const settle = () => {
      if (document.visibilityState !== "visible") return;
      frame();
    };
    document.addEventListener("visibilitychange", settle);

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: runwayRef.current,
        start: "top bottom",
        end: "bottom top",
        onUpdate: frame,
        onRefresh: () => {
          measureStop();
          frame();
        },
      });
    }, runwayRef);

    return () => {
      document.removeEventListener("visibilitychange", settle);
      runwayRef.current?.style.removeProperty("--card-stop");
      cardEls.current.forEach((c) => {
        if (!c) return;
        c.style.opacity = "";
        c.style.transform = "";
      });
      ctx.revert();
    };
  }, [tiers, cumulative, segments, poolTotal, reduceMotion]);

  const header = (
    <div className="mx-auto flex w-[min(149.25rem,100%)] flex-col items-center gap-[3.375rem] px-[3rem] pb-[6.45rem] pt-[8rem] text-center lg:gap-[3.125rem] lg:pb-[20rem] lg:pt-[17.5rem]">
      {chip && (
        <FadeIn delay={0}>
          <PipeChip className="m-0">{chip}</PipeChip>
        </FadeIn>
      )}
      <div className="flex w-full flex-col items-center gap-[1.625rem]">
        {title && (
          <FadeIn delay={0.1} className="w-[min(80rem,100%)]">
            <FitHeadline className="richtext richtext-center w-full [--fit:1] [--h:4.5rem] [text-wrap:balance] lg:[--h:6rem] prose-p:m-0 prose-p:font-news prose-p:[font-size:calc(var(--h)*var(--fit))] prose-p:font-normal prose-p:leading-[1.1] prose-p:tracking-[-0.25rem] prose-p:text-white/90 prose-headings:m-0 prose-headings:font-news prose-headings:[font-size:calc(var(--h)*var(--fit))] prose-headings:font-normal prose-headings:leading-[1.1] prose-headings:tracking-[-0.25rem] prose-headings:text-white/90 prose-em:font-news prose-em:italic">
              <PortableText value={title} components={richTextComponent} />
            </FitHeadline>
          </FadeIn>
        )}
        {subtitle && (
          <FadeIn delay={0.15} className="w-[min(57.5rem,100%)]">
            <p className="m-0 font-geist text-[2.25rem] leading-[1.5] text-white/60">
              {subtitle}
            </p>
          </FadeIn>
        )}
      </div>
    </div>
  );

  if (items.length === 0) {
    return <section className="relative w-full overflow-hidden bg-black">{header}</section>;
  }

  return (
    <section className="relative w-full overflow-x-clip bg-black">
      {header}

      <div ref={runwayRef} className="relative mx-auto w-[min(180rem,100%)]">
        <div className="pointer-events-none absolute inset-x-0 bottom-[8rem] top-0 z-0">
          <div className="sticky top-[calc(50svh-28.6875rem)] mx-auto aspect-square w-[min(57.375rem,86%)] max-lg:top-[8rem]">
            <PoolRing
              ringRef={ringRef}
              fillEls={fillEls}
              segments={segments}
              amount={amount}
              still={reduceMotion}
              poolLabel={poolLabel}
              poolTotal={poolTotal}
              poolPlaces={poolPlaces}
            />
          </div>
        </div>

        <div className="relative z-10 pb-[calc(45svh_+_8rem)] pt-[26svh] max-lg:flex max-lg:flex-col max-lg:items-center max-lg:px-[3rem] max-lg:pb-0 max-lg:pt-[75svh]">
          {items.map((tier, i) => (
            <div
              key={i}
              className={cn(
                "flex h-[70svh] items-center px-[3rem] max-lg:contents lg:px-[10rem]",
                i % 2 === 0 ? "justify-start" : "justify-end",
              )}
            >
              {/* Contents on mobile, so every card shares the runway as its sticky container. */}
              <div
                className={cn(
                  "w-[min(40.75rem,100%)] will-change-transform max-lg:sticky max-lg:top-[var(--card-stop,calc(13.625rem_+_min(57.375rem,86vw)))] max-lg:w-[min(44.5rem,100%)] max-lg:origin-top",
                  i > 0 && "max-lg:mt-[30svh]",
                )}
                ref={(el) => {
                  cardEls.current[i] = el;
                }}
              >
                <TierCard tier={tier} index={i} />
              </div>
            </div>
          ))}
          <div aria-hidden className="hidden max-lg:block max-lg:h-[10svh]" />
        </div>
      </div>
    </section>
  );
}
